import { LayoutDashboard, LogOut, Settings, Wand2 } from "lucide-react";
import type { ReactNode } from "react";
import { Link, NavLink, useNavigate } from "react-router-dom";

import { useLogout } from "@/api/hooks";
import { useAuth } from "@/auth/AuthProvider";
import { Button } from "@/components/ui/button";

interface NavItem {
	to: string;
	label: string;
	icon: typeof LayoutDashboard;
}

const NAV_ITEMS: NavItem[] = [
	{ to: "/", label: "Clients", icon: LayoutDashboard },
	{ to: "/onboarding", label: "Onboarding", icon: Wand2 },
	{ to: "/settings", label: "Settings", icon: Settings },
];

/** Shared two-column layout for authenticated pages. Left-side nav,
 * right-side main content. Header strip shows admin identity + logout. */
export function Layout({ children }: { children: ReactNode }) {
	const { admin } = useAuth();
	const logout = useLogout();
	const navigate = useNavigate();

	async function onLogout() {
		try {
			await logout.mutateAsync();
		} finally {
			navigate("/login", { replace: true });
		}
	}

	return (
		<div className="flex min-h-screen bg-muted/20">
			<aside className="hidden w-60 flex-col border-r bg-background md:flex">
				<div className="flex h-14 items-center gap-2 border-b px-6">
					<Link to="/" className="font-semibold tracking-tight">
						Veridon Ops
					</Link>
				</div>
				<nav className="flex-1 space-y-1 p-2">
					{NAV_ITEMS.map((item) => (
						<NavLink
							key={item.to}
							to={item.to}
							end
							className={({ isActive }) =>
								`flex items-center gap-3 rounded-md px-3 py-2 text-sm transition-colors ${
									isActive
										? "bg-accent text-accent-foreground font-medium"
										: "text-muted-foreground hover:bg-accent/50 hover:text-foreground"
								}`
							}
						>
							<item.icon className="h-4 w-4" aria-hidden />
							{item.label}
						</NavLink>
					))}
				</nav>
			</aside>
			<div className="flex flex-1 flex-col">
				<header className="flex h-14 items-center justify-between border-b bg-background px-6">
					<h1 className="text-sm font-medium text-muted-foreground">Aggregator dashboard</h1>
					<div className="flex items-center gap-3 text-sm">
						<span className="text-muted-foreground">{admin?.email}</span>
						<Button size="sm" variant="outline" onClick={onLogout} disabled={logout.isPending}>
							<LogOut className="mr-1 h-3 w-3" aria-hidden />
							{logout.isPending ? "Signing out…" : "Sign out"}
						</Button>
					</div>
				</header>
				<main className="flex-1 p-6">{children}</main>
			</div>
		</div>
	);
}
